import type { ImageVariantSources } from "@/lib/image-variants";
import categoriesJson from "@/data/blog/lib/categories.json";
import homeIndexJson from "@/data/blog/lib/blogs/home-index.json";

import { getBlogCategoryLabel } from "@/lib/blog/category-label";
import { formatBlogMinRead, resolveReadTimeMinutes } from "@/lib/blog/read-time";
import { getBlogPageCopy } from "@/lib/blog-page-copy";
import { buildBlogPostHref } from "@/lib/i18n/paths";
import { getBlogPostSlug } from "@/lib/blog/post-locale";
import type { Lang } from "@/lib/types";

export type BlogCategory = {
  _id: string;
  name?: string | Record<string, string>;
  slug?: string | Record<string, string>;
};

export type BlogPostRecord = Record<string, unknown> & {
  _id: string | { toString(): string };
  slug?: string;
  localizedSlugs?: Record<string, string>;
  seoDescription?: Record<string, string>;
  title?: string | Record<string, string>;
  intro?: string | Record<string, string>;
  description?: string | Record<string, string>;
  content?: string | Record<string, string>;
  date?: string;
  createdAt?: string;
  readTime?: number;
  category_ids?: string[];
  img?: { src?: string; alt?: string | Record<string, string> };
  url?: string | Record<string, string>;
  relatedPosts?: string[];
  updatedAt?: string;
  /** Editorial posts: the only languages the post is published in (e.g. `["sv"]`). */
  languages?: string[];
  /**
   * Editorial posts: slugs of the same article written as a separate post in
   * another language, e.g. `{ "sv": "vad-ar-en-bra-roas" }` on the Norwegian
   * post. Linked both ways for hreflang, the sitemap and the language switcher.
   */
  translations?: Record<string, string>;
  /** Explicit semantic subject for product-specific articles; translations must share it. */
  translationIdentity?: string;
  /**
   * Editorial posts: ID of the synced post whose URL this article takes over in
   * its `languages`. That synced post's version in those languages is retired
   * (no card, alternate or sitemap entry); its other languages stay live.
   */
  supersedes?: string;
  /** Editorial posts: shorter `<title>` than the H1, per language. */
  seoTitle?: Record<string, string>;
  /** Editorial posts: Q&A shown under the article and as FAQPage schema. */
  faq?: Record<string, BlogPostFaqItem[]>;
  /** Repo-managed per-language modified dates (synced-post overrides); falls back to `updatedAt`. */
  modifiedAt?: Record<string, string>;
  /** Editorial posts: cited sources listed under the article. */
  sources?: Record<string, BlogPostSource[]>;
};

export type BlogPostFaqItem = { question: string; answer: string };

export type BlogPostSource = { title: string; url: string; publisher: string };

export type BlogListCard = {
  id: string;
  slug: string;
  href: string;
  title: string;
  excerpt: string;
  date: string;
  dateTime: string;
  category: string;
  image: string;
  /** Only this card's pipeline candidates, prepared on the server. */
  imageSources?: ImageVariantSources;
  readTime?: number;
  readTimeText?: string;
};

export function pickBlogLocalized(
  value: string | Record<string, string> | undefined,
  lang: Lang,
): string {
  if (!value) return "";
  if (typeof value === "string") return value;
  return value[lang] ?? value.en ?? Object.values(value)[0] ?? "";
}

function normalizeDate(
  raw: string | undefined,
  lang: Lang,
): { display: string; iso: string } {
  if (!raw) {
    return { display: "", iso: "" };
  }
  const parsed = new Date(raw);
  if (Number.isNaN(parsed.getTime())) {
    return { display: raw, iso: raw.slice(0, 10) };
  }
  return {
    display: parsed.toLocaleDateString(getBlogPageCopy(lang).dateLocale, {
      month: "long",
      day: "numeric",
      year: "numeric",
    }),
    iso: parsed.toISOString().slice(0, 10),
  };
}

/** Mongo and synced JSON may store dates as ISO strings or `Date` instances. */
export function coerceBlogDateInput(value: unknown): string | undefined {
  if (value == null) return undefined;
  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? undefined : value.toISOString();
  }
  if (typeof value === "string") {
    const trimmed = value.trim();
    return trimmed || undefined;
  }
  if (typeof value === "number" && Number.isFinite(value)) {
    const parsed = new Date(value);
    return Number.isNaN(parsed.getTime()) ? undefined : parsed.toISOString();
  }
  return undefined;
}

export function resolvePostListDate(
  post: BlogPostRecord,
  lang: Lang = "en",
): { display: string; iso: string } {
  const raw =
    coerceBlogDateInput(post.date) ??
    coerceBlogDateInput(post.createdAt) ??
    coerceBlogDateInput(post.updatedAt);
  return normalizeDate(raw, lang);
}

function categoryLabel(categoryId: string | undefined, lang: Lang): string {
  const fallback = getBlogPageCopy(lang).categoryFallback;
  if (!categoryId) return fallback;
  const categories = categoriesJson as BlogCategory[];
  const hit = categories.find((c) => c._id === categoryId);
  if (!hit) return fallback;
  return getBlogCategoryLabel(hit, lang) || fallback;
}

function cardImageForPost(post: BlogPostRecord, index: number): string {
  if (post.img?.src) return post.img.src;
  const fallback = [
    "/assets/imgs/blog-preview/post-01.png",
    "/assets/imgs/blog-preview/post-02.png",
    "/assets/imgs/blog-preview/post-03.png",
  ];
  return fallback[index % fallback.length];
}

function postIdString(post: { _id: string | { toString(): string } }): string {
  const id = post._id;
  return typeof id === "string" ? id : id.toString();
}

function rawHomeIndexEntries(): BlogPostRecord[] {
  const root = homeIndexJson as BlogPostRecord[] | { sixCardGrid?: BlogPostRecord[] };
  if (Array.isArray(root)) return root;
  if (root && typeof root === "object" && Array.isArray(root.sixCardGrid)) {
    return root.sixCardGrid;
  }
  return [];
}

function cardFromIndexEntry(entry: BlogPostRecord, lang: Lang, index: number): BlogListCard {
  const id = postIdString(entry);
  const slug = getBlogPostSlug(entry, lang);
  const { display, iso } = resolvePostListDate(entry, lang);
  const localizedUrl = pickBlogLocalized(entry.url, lang);
  const href =
    localizedUrl.startsWith("http://") || localizedUrl.startsWith("https://")
      ? localizedUrl
      : buildBlogPostHref(slug, lang);
  const readTime = resolveReadTimeMinutes(entry);
  return {
    id,
    slug,
    href,
    title: pickBlogLocalized(entry.title, lang),
    excerpt:
      pickBlogLocalized(entry.intro, lang) ||
      pickBlogLocalized(entry.description, lang),
    date: display,
    dateTime: iso,
    category: categoryLabel(entry.category_ids?.[0], lang),
    image: cardImageForPost(entry, index),
    readTime,
    readTimeText: readTime > 0 ? formatBlogMinRead(readTime, lang) : undefined,
  };
}

/** Client-safe preview cards from synced `home-index.json` (no filesystem reads). */
export function getHomeBlogPreviewCardsFromIndex(lang: Lang, limit = 4): BlogListCard[] {
  return rawHomeIndexEntries()
    .filter((entry) => !entry.languages || entry.languages.includes(lang))
    .slice(0, limit)
    .map((entry, index) => cardFromIndexEntry(entry, lang, index));
}
